import { apiFetch } from "@d12labs/client";
import { Badge } from "@d12labs/ui/badge";
import { useMutation } from "@tanstack/react-query";
import type React from "react";
import { toast } from "sonner";
import type { SectionKey } from "../hooks/use-meeting-detail";
import type { CategoryKey, NotableQuote } from "../types";
import { CATEGORY_LABELS } from "../types";
import { ActionItems } from "./action-items";
import { scoreToneClass } from "./score-bar";

function Kbd({ children }: { children: React.ReactNode }) {
	return (
		<span className="inline-flex h-[18px] items-center rounded border border-hairline bg-surface-2 px-1.5 font-mono text-[10px] text-ink-subtle">
			{children}
		</span>
	);
}

interface DimContent {
	score: number;
	analysis: string;
	wins: string[];
	changes: string[];
}

interface SectionStreamProps {
	speakerName: string;
	/** The active dimension (null = show sections stream; non-null = show dim detail). */
	activeDim: CategoryKey | null;
	/** Which section tab is active (only used when activeDim is null). */
	section: SectionKey;
	/** Full per-dim content map keyed by CategoryKey. */
	dimContentMap: Record<CategoryKey, DimContent | null>;
	/** Quotes filtered to the active dimension (for dim-detail aside). */
	activeQuotes: NotableQuote[];
	audioRef: React.RefObject<HTMLAudioElement | null>;
	onSeeInTranscript: () => void;
	executiveSummary: string | null;
	strengths: string[];
	improvements: string[];
	allQuotes: NotableQuote[];
	/** Meeting metadata for the "Coach me" CTA. */
	meetingTitle: string;
	meetingDate: string;
	/** Markdown prose blob from meeting metadata. Null when absent. */
	actionItems?: string | null;
	/** Current user's display name for "You" badge in action items (best-effort). */
	meName?: string | null;
}

function sentimentTone(s: string): "success" | "danger" | "subtle" {
	if (s === "positive") return "success";
	if (s === "negative") return "danger";
	return "subtle";
}

function sentimentDot(s: string): string {
	if (s === "positive") return "var(--color-success)";
	if (s === "negative") return "var(--color-danger)";
	return "var(--color-ink-tertiary)";
}

function formatTimestamp(sec: number): string {
	const m = Math.floor(sec / 60);
	const s = Math.floor(sec % 60);
	return `${m}:${s.toString().padStart(2, "0")}`;
}

const SECTION_HEADER = "text-eyebrow uppercase text-ink-tertiary";

export function SectionStream({
	speakerName,
	activeDim,
	section,
	dimContentMap,
	activeQuotes,
	audioRef,
	onSeeInTranscript,
	executiveSummary,
	strengths,
	improvements,
	allQuotes,
	meetingTitle,
	meetingDate,
	actionItems,
	meName,
}: SectionStreamProps) {
	const firstName = speakerName.split(" ")[0] ?? speakerName;

	const handleQuoteClick = (q: NotableQuote) => {
		if (audioRef.current && q.timestampSec != null) {
			audioRef.current.currentTime = q.timestampSec;
		}
	};

	// coachMutation is only rendered when activeDim !== null — safe to define here
	const coachMutation = useMutation({
		mutationFn: () => {
			if (!activeDim) return Promise.reject(new Error("No active dimension"));
			return apiFetch<{ drill: { title: string }; rationale: string }>(
				"/api/plugins/speech-coach/plan/drills",
				{
					method: "POST",
					body: JSON.stringify({
						dimensionKey: activeDim,
						quotes: activeQuotes.map((q) => ({
							text: q.quote,
							sentiment: q.sentiment,
							timestampSec: q.timestampSec,
						})),
						meetingTitle,
						meetingDate,
					}),
				},
			);
		},
		onSuccess: (res) => {
			toast.success(`Added "${res.drill.title}" — ${res.rationale}`);
		},
		onError: (err) => {
			const msg = (err as Error).message;
			if (msg.includes("no active plan")) {
				toast.error("No active plan yet — start one in Speech Coach first.");
			} else {
				toast.error(msg);
			}
		},
	});

	// ── When a dimension is active, show only its detail (replaces the stream) ──
	if (activeDim !== null) {
		const content = dimContentMap[activeDim];
		const dimLabel = CATEGORY_LABELS[activeDim];
		const score = content?.score ?? 0;

		return (
			<div className="overflow-y-auto">
				{/* Sticky header */}
				<div className="sticky top-0 z-10 border-b border-hairline bg-canvas/95 px-8 pt-6 pb-3 backdrop-blur">
					<div className={SECTION_HEADER}>
						{firstName.toUpperCase()} · {dimLabel.toUpperCase()}
					</div>
					<div className="mt-1 flex items-baseline gap-3">
						<h2 className="text-[28px] font-semibold leading-none tracking-tight text-ink">
							{dimLabel}
						</h2>
						<span className={`text-[20px] font-semibold tabular-nums ${scoreToneClass(score)}`}>
							{score.toFixed(1)}
						</span>
						<span className="text-body-sm text-ink-tertiary">/10</span>
						<span className="ml-auto flex items-center gap-2 text-meta text-ink-subtle">
							<Kbd>J</Kbd>/<Kbd>K</Kbd> cycle dims · <Kbd>T</Kbd> jump to transcript
						</span>
					</div>
				</div>

				<div className="px-8 py-6">
					{content ? (
						<div className="grid grid-cols-[1fr_320px] items-start gap-6">
							<div className="space-y-5">
								{content.analysis ? (
									<div>
										<div className={`mb-2 ${SECTION_HEADER}`}>Analysis</div>
										<p className="text-body-lg leading-relaxed text-ink-muted">
											{content.analysis}
										</p>
									</div>
								) : null}

								{content.wins.length > 0 ? (
									<div>
										<div className="mb-2 flex items-center justify-between">
											<div className={SECTION_HEADER}>What's working</div>
											<button
												type="button"
												onClick={() => coachMutation.mutate()}
												disabled={coachMutation.isPending}
												className="text-meta text-ink-subtle hover:text-ink disabled:opacity-50"
											>
												{coachMutation.isPending ? "Adding…" : "+ Coach me"}
											</button>
										</div>
										<ul className="list-none space-y-1.5 pl-0 text-body-sm text-ink-muted">
											{content.wins.map((s) => (
												<li key={s} className="flex gap-2">
													<span className="mt-1.5 size-2 flex-none rounded-full bg-semantic-success" />
													{s}
												</li>
											))}
										</ul>
									</div>
								) : null}

								{content.changes.length > 0 ? (
									<div>
										<div className={`mb-2 ${SECTION_HEADER}`}>Specific changes</div>
										<ul className="list-none space-y-1.5 pl-0 text-body-sm text-ink-muted">
											{content.changes.map((s) => (
												<li key={s} className="flex gap-2">
													<span className="mt-1.5 size-2 flex-none rounded-full bg-warning" />
													{s}
												</li>
											))}
										</ul>
									</div>
								) : null}
							</div>

							<aside className="space-y-3">
								<div className="flex items-baseline justify-between">
									<span className={SECTION_HEADER}>Quotes · {dimLabel}</span>
									<button
										type="button"
										onClick={onSeeInTranscript}
										className="text-meta text-ink-subtle hover:text-ink"
									>
										See in transcript →
									</button>
								</div>
								{activeQuotes.length > 0 ? (
									activeQuotes.map((q) => (
										<button
											key={`${q.timestampSec}-${q.sentiment}`}
											type="button"
											onClick={() => handleQuoteClick(q)}
											className="w-full cursor-pointer rounded-md border border-hairline bg-canvas p-3 text-left hover:border-hairline-strong"
										>
											<div className="mb-1.5 flex items-center justify-between">
												<Badge tone={sentimentTone(q.sentiment)} dot={sentimentDot(q.sentiment)}>
													{q.sentiment}
												</Badge>
												{q.timestampSec != null ? (
													<span className="font-mono text-meta text-ink-subtle">
														{formatTimestamp(q.timestampSec)}
													</span>
												) : null}
											</div>
											<p className="text-body-sm italic text-ink-muted">"{q.quote}"</p>
										</button>
									))
								) : (
									<div className="rounded-md border border-dashed border-hairline p-4 text-center">
										<p className="text-meta text-ink-subtle">
											No annotations on this dimension yet.
										</p>
									</div>
								)}
							</aside>
						</div>
					) : (
						<p className="text-body-sm text-ink-subtle">No analysis for this dimension.</p>
					)}
				</div>
			</div>
		);
	}

	// ── Action items view (activeDim === null && section === "action-items") ────
	if (section === "action-items") {
		return (
			<div className="overflow-y-auto">
				{/* ── Sticky header ─────────────────────────────────────────────── */}
				<div className="sticky top-0 z-10 border-b border-hairline bg-canvas/95 px-8 pt-6 pb-3 backdrop-blur">
					<div className={SECTION_HEADER}>{firstName.toUpperCase()} · ACTION ITEMS</div>
					<div className="mt-1 flex items-baseline gap-3">
						<h2 className="text-[28px] font-semibold leading-none tracking-tight text-ink">
							Action items
						</h2>
						<span className="ml-auto flex items-center gap-2 text-meta text-ink-subtle">
							<Kbd>J</Kbd>/<Kbd>K</Kbd> cycle dims · <Kbd>T</Kbd> jump to transcript
						</span>
					</div>
				</div>

				<div className="px-8 py-6">
					<ActionItems
						source={actionItems ?? null}
						onJump={(sec) => {
							if (audioRef.current) audioRef.current.currentTime = sec;
						}}
						meName={meName}
					/>
				</div>
			</div>
		);
	}

	// ── Summary stream (activeDim === null && section === "summary") ───────────
	return (
		<div className="overflow-y-auto">
			{/* ── Sticky header ─────────────────────────────────────────────── */}
			<div className="sticky top-0 z-10 border-b border-hairline bg-canvas/95 px-8 pt-6 pb-3 backdrop-blur">
				<div className={SECTION_HEADER}>{firstName.toUpperCase()} · ANALYSIS</div>
				<div className="mt-1 flex items-baseline gap-3">
					<h2 className="text-[28px] font-semibold leading-none tracking-tight text-ink">
						{firstName}
					</h2>
					<span className="ml-auto flex items-center gap-2 text-meta text-ink-subtle">
						<Kbd>J</Kbd>/<Kbd>K</Kbd> cycle dims · <Kbd>T</Kbd> jump to transcript
					</span>
				</div>
			</div>

			<div className="px-8 py-6 space-y-8">
				{/* ── Summary ─────────────────────────────────────────────────── */}
				<section>
					<div className={`mb-3 ${SECTION_HEADER}`}>Summary</div>
					{executiveSummary ? (
						<p className="max-w-3xl text-body-lg leading-relaxed text-ink-muted">
							{executiveSummary}
						</p>
					) : (
						<p className="text-body-sm text-ink-subtle">No summary recorded.</p>
					)}
				</section>

				{/* ── Strengths ───────────────────────────────────────────────── */}
				<section>
					<div className={`mb-3 flex items-center gap-2 ${SECTION_HEADER}`}>
						<span>Strengths</span>
						{strengths.length > 0 ? (
							<span className="text-meta text-ink-subtle normal-case tracking-normal font-normal">
								{strengths.length}
							</span>
						) : null}
					</div>
					{strengths.length > 0 ? (
						<ul className="max-w-3xl list-none space-y-3 pl-0 text-body text-ink-muted">
							{strengths.map((s) => (
								<li key={s} className="flex gap-3">
									<span className="mt-2 size-2 flex-none rounded-full bg-semantic-success" />
									<span className="leading-relaxed">{s}</span>
								</li>
							))}
						</ul>
					) : (
						<p className="text-body-sm text-ink-subtle">No strengths recorded.</p>
					)}
				</section>

				{/* ── Improvements ────────────────────────────────────────────── */}
				<section>
					<div className={`mb-3 flex items-center gap-2 ${SECTION_HEADER}`}>
						<span>Improvements</span>
						{improvements.length > 0 ? (
							<span className="text-meta text-ink-subtle normal-case tracking-normal font-normal">
								{improvements.length}
							</span>
						) : null}
					</div>
					{improvements.length > 0 ? (
						<ul className="max-w-3xl list-none space-y-3 pl-0 text-body text-ink-muted">
							{improvements.map((s) => (
								<li key={s} className="flex gap-3">
									<span className="mt-2 size-2 flex-none rounded-full bg-warning" />
									<span className="leading-relaxed">{s}</span>
								</li>
							))}
						</ul>
					) : (
						<p className="text-body-sm text-ink-subtle">No improvements recorded.</p>
					)}
				</section>

				{/* ── Notable quotes ──────────────────────────────────────────── */}
				<section>
					<div className={`mb-3 flex items-center gap-2 ${SECTION_HEADER}`}>
						<span>Notable quotes</span>
						{allQuotes.length > 0 ? (
							<span className="text-meta text-ink-subtle normal-case tracking-normal font-normal">
								{allQuotes.length}
							</span>
						) : null}
					</div>
					{allQuotes.length > 0 ? (
						<div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
							{allQuotes.map((q, i) => (
								<button
									// The same line is often cited under multiple dimensions (e.g. a
									// short "Yes." for both Clarity and Confidence at one timestamp),
									// so (timestamp, quote) is NOT unique — the list index is the only
									// stable tiebreaker. allQuotes is a fixed, non-reordered per-speaker list.
									// biome-ignore lint/suspicious/noArrayIndexKey: notable quotes can legitimately duplicate; index disambiguates a stable list
									key={`${q.timestampSec}-${q.dimensionKey}-${i}`}
									type="button"
									onClick={() => handleQuoteClick(q)}
									className="cursor-pointer rounded-md border border-hairline bg-surface-1 p-3 text-left hover:border-hairline-strong"
								>
									<div className="mb-1.5 flex items-center justify-between gap-2">
										<Badge tone={sentimentTone(q.sentiment)} dot={sentimentDot(q.sentiment)}>
											{q.sentiment}
										</Badge>
										<span className="truncate text-meta text-ink-subtle">
											{CATEGORY_LABELS[q.dimensionKey]}
										</span>
										<span className="font-mono text-meta text-ink-subtle">
											{formatTimestamp(q.timestampSec)}
										</span>
									</div>
									<p className="text-body-sm italic text-ink-muted">"{q.quote}"</p>
								</button>
							))}
						</div>
					) : (
						<p className="text-body-sm text-ink-subtle">No notable quotes recorded.</p>
					)}
				</section>
			</div>
		</div>
	);
}
